import { useEffect, useState, useRef, useCallback } from "react";
import Select from "react-select";
import {
  saveToState,
  loadFromState,
  resetState,
  formateTimeRange,
} from "../utils";
import { getProfiles } from "../api/quontxApi";

const ProfileFilter = ({ entities, proxies, isLoading, propagnateData }) => {
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [timestampBuckets, setTimestampBuckets] = useState([]);

  const [activeNode, setActiveNode] = useState(null);
  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);

  const resetFilters = () => {
    resetState("profileCompareState", [
      {
        stateFunc: setActiveNode,
        nullVal: null,
      },
      {
        stateFunc: setActiveTimestampBucket,
        nullVal: null,
      },
    ]);
    setData(null);
  };

  const loadProfile = useCallback(async () => {
    const profiles = await getProfiles({
      itemsPerPage: 1,
      entities: [activeNode.value],
    });
    propagnateFunctionRef.current(profiles.data);
    setData(profiles.data.data[0]);
    setTimestampBuckets(profiles.data.data[0].metadata.corpora.time_intervals);
    setActiveTimestampBucket(null);
  }, [activeNode]);

  useEffect(() => {
    loadFromState("profileCompareState", [
      {
        stateFunc: setActiveNode,
        storageObjectKey: "activeNode",
        defaultVal: null,
      },
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (activeNode !== null) {
      saveToState("profileCompareState", {
        activeNode: activeNode,
        activeTimestampBucket: activeTimestampBucket,
      });
    }
  }, [activeNode]);


  useEffect(() => {
    if (!activeNode) return;
    (async () => {
      setLoadingData(true);
      await loadProfile();
      setLoadingData(false);
    })();
  }, [activeNode, loadProfile]);

  return (
    <div className="w-full flex flex-col items-start content-center">
      {isLoading && <p>Loading data...</p>}
      {!isLoading && entities && proxies && (
        <div className=" ml-[120px]">
          <div className="flex flex-col gap-4 items-start justify-center mb-8">
            <div className="flex gap-4  justify-center mb-8">
              <div className="w-[240px] flex flex-col gap-2">
                <p>Node</p>
                <Select
                  isSearchable={true}
                  value={activeNode}
                  onChange={(selected) => setActiveNode(selected)}
                  options={[...entities, ...proxies].map((node) => {
                    return {
                      label: node.name,
                      value: node.uuid,
                    };
                  })}
                />
              </div>
            </div>
            <div>
              <button
                onClick={() => resetFilters()}
                className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
              >
                Reset filters
              </button>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-6">
            Profile Statistics
          </h1>
          {data && (
            <div>
              <div className="flex items-center content-center flex-wrap">
                {loadingData && <p>Loading data...</p>}
                {!loadingData && (
                  <div>
                    <div className="bg-qtx-darker shadow-lg rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <thead className="bg-qtx-dark">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider font-bold">
                              Profile index
                            </th>
                            {data.metadata.corpora.time_intervals
                              .slice()
                              .reverse()
                              .flatMap((timeInterval, idx) => {
                                if (idx > 3) return;
                                return (
                                  <th
                                    key={idx}
                                    className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider max-w-64 font-bold"
                                  >
                                    {formateTimeRange(timeInterval)}
                                  </th>
                                );
                              })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-qtx-gray">
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                              Coverage Index
                            </td>
                            {console.log(data)}
                            {
                            data.profile_stats.coverage_index
                              .slice()
                              .reverse()
                              .flatMap((stat, idx) => {
                                if (idx > 3) return;
                                return (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat}
                                  </td>
                                );
                              })}
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray">
                              Intensity Index
                            </td>
                            {data.profile_stats.intensity_index
                              .slice()
                              .reverse()
                              .flatMap((stat, idx) => {
                                if (idx > 3) return;
                                return (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat}
                                  </td>
                                );
                              })}
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                              Gini coefficient
                            </td>
                            {data.profile_stats.gini_coefficient
                              .slice()
                              .reverse()
                              .flatMap((stat, idx) => {
                                if (idx > 3) return;
                                return (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat}
                                  </td>
                                );
                              })}
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                              Dispersion Index
                            </td>
                            {data.profile_stats.dispersion_index
                              .slice()
                              .reverse()
                              .flatMap((stat, idx) => {
                                if (idx > 3) return;
                                return (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat}
                                  </td>
                                );
                              })}
                          </tr>
                          <tr>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                              Concentration ratio
                            </td>
                            {data.profile_stats.concentration_ratio
                              .slice()
                              .reverse()
                              .flatMap((stat, idx) => {
                                if (idx > 3) return;
                                return (
                                  <td
                                    key={idx}
                                    className="px-6 py-4 whitespace-nowrap text-sm font-mono text-qtx-light-gray"
                                  >
                                    {stat}
                                  </td>
                                );
                              })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="flex flex-col gap-4 items-start justify-center mb-8 mt-16">
                      <h2 className="text-3xl font-bold text-white mb-6">
                        Top 10 Societal Dimensions
                      </h2>
                      <div className="flex gap-4  justify-center mb-8">
                        <div className="w-[240px] flex flex-col gap-2">
                          <p>Timestamp Bucket</p>
                          <Select
                            isDisabled={!data}
                            styles={{
                              control: (baseStyles, state) => ({
                                ...baseStyles,
                                borderColor: !data ? "red" : "grey",
                              }),
                            }}
                            isSearchable={false}
                            value={{
                              label: activeTimestampBucket,
                              value: activeTimestampBucket,
                            }}
                            onChange={(selected) =>
                              setActiveTimestampBucket(selected.value)
                            }
                            options={timestampBuckets.map((timebucket, idx) => {
                              return {
                                label: formateTimeRange(timebucket),
                                value: timebucket,
                              };
                            })}
                          />
                        </div>
                      </div>
                    </div>
                    {activeTimestampBucket && (
                      <div>
                        <ol type="1" className="list-decimal">
                          {Object.values(data.profile)
                            .sort((a, b) => {
                              if (
                                a.value[
                                  timestampBuckets.indexOf(
                                    activeTimestampBucket
                                  )
                                ] >
                                b.value[
                                  timestampBuckets.indexOf(
                                    activeTimestampBucket
                                  )
                                ]
                              )
                                return -1;
                              if (
                                a.value[
                                  timestampBuckets.indexOf(
                                    activeTimestampBucket
                                  )
                                ] <
                                b.value[
                                  timestampBuckets.indexOf(
                                    activeTimestampBucket
                                  )
                                ]
                              )
                                return 1;
                              return 0;
                            })
                            .flatMap((dimension, idx) => {
                              if (
                                idx > 9 ||
                                dimension.value[
                                  timestampBuckets.indexOf(
                                    activeTimestampBucket
                                  )
                                ] === 0
                              )
                                return;
                              return (
                                <li key={dimension.label}>{` ${
                                  dimension.label
                                } (${
                                  dimension.value[
                                    timestampBuckets.indexOf(
                                      activeTimestampBucket
                                    )
                                  ]
                                })`}</li>
                              );
                            })}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileFilter;
